import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";
import type { WorkUnitKind } from "./adminWorkUnit";

// 현재 producer가 보내는 known job 종류다. wire는 앞으로 추가될 worker kind를 raw로 표시할 수
// 있도록 열린 문자열로 받는다.
export type WorkerCurrentJobKind =
  | "upload"
  | "merge"
  | "annotation_materialize"
  | "annotation_bundle_index"
  | "storage_cleanup"
  | "diagnostics";

export type WorkerJobProgress = {
  done: number;
  total: number | null;
  unit: string;
};

// Worker 가 지금 점유한 작업 단위다. 단위가 작업 전체(job)면 ordinal 은 null 이다.
// total 은 작업 단위 요약과 같은 3-상태다(`readWorkUnitTotal`).
export type WorkerJobUnit = {
  kind: WorkUnitKind;
  ordinal: number | null;
  total: number | null;
  total_final: boolean;
};

// registry 가 목록을 조회할 때 current_job 에 채우는 작업 정보다. 워커는 이 값을 보내지 않는다.
// 작업 행을 찾지 못하면 null 이다. 예) 작업이 지워졌거나 kind 를 모른다.
export type WorkerJobSubject = {
  // 진단은 parquet·aggregate·video 로 나눠 준다. 업로드는 upload 다. 그 밖의 kind 는 raw 문자열이다.
  job_kind: string;
  dataset_uuid: string | null;
  dataset_display_name: string | null;
  unit: WorkerJobUnit | null;
};

// 워커가 현재 처리 중인 job 디스크립터다. idle이면 status.current_job이 null이다.
export type WorkerCurrentJob = {
  kind: string;
  uuid: string;
  // producer가 보고한 도메인 상태다. 상태 축이 없으면 null이다.
  state: string | null;
  // 워커가 job을 가져간 시각(ISO-8601 UTC)이다. "N초째 처리 중"을 계산하는 기준 시각이다.
  started_at: string;
  generation?: number | null;
  progress?: WorkerJobProgress | null;
  subject: WorkerJobSubject | null;
};

export type WorkerDiskUsage = {
  total_bytes: number;
  used_bytes: number;
  free_bytes: number;
  used_percent: number;
};

export type WorkerLoadAvg = {
  "1m": number;
  "5m": number;
  "15m": number;
};

// scratch root 바로 아래 항목 하나다. 워커는 크기를 재지 않는다. 이름과 마지막 변경 이후
// 경과 초만 보고한다. 운영자는 이 경과 시간으로 끝난 작업이 남긴 디렉터리를 찾는다.
export type WorkerScratchEntry = {
  name: string;
  age_seconds: number;
};

// 서버가 알려진 health 필드를 검증한다. 보존된 과거 보고에 값이 없으면 미보고로 표시한다.
export type WorkerStatusBlob = {
  scratch_dir?: string;
  disk?: WorkerDiskUsage;
  loadavg?: WorkerLoadAvg;
  scratch_entry_count?: number;
  scratch_entries?: WorkerScratchEntry[];
  current_job?: WorkerCurrentJob | null;
  last_progress_at?: string;
  queues?: string[];
  // 그 워커가 선언한 저장 위치다. 값은 `gcp_storage`, `azure_blob` 이다. 서버는 업로드 잡과 회수 잡을
  // 그 잡의 저장 위치를 선언한 워커에게만 준다. 이 값을 보고하지 않는 워커는 빈 목록이다. 자유
  // 문자열로 받는다. 워커가 화면보다 먼저 새 값을 보고할 수 있다.
  storage_backends?: string[];
};

// admin worker status 목록의 한 워커다. 서버는 보존 윈도우(12h) 안에 보고한 워커를 반환한다.
// 지금 죽었거나 재시작된 워커도 윈도우 동안 마지막 상태가 남는다. online/offline은
// 서버가 계산한 is_online 을 그대로 쓴다.
export type WorkerHeartbeat = {
  worker_id: string;
  status: WorkerStatusBlob;
  // 워커가 status를 보고한 시각(ISO-8601 UTC).
  reported_at: string;
  // 보고 후 경과 초(서버 기준)다. 표시에만 쓴다.
  age_seconds: number;
  // 서버가 계산한 online 여부다(heartbeat 주기 x4 윈도우). 소비처는 매직넘버 없이 이 값을 쓴다.
  is_online: boolean;
};

export type WorkerStatusListResp = {
  observed_at: string;
  workers: WorkerHeartbeat[];
};

// 워커의 작업 상태다. `current_job` 과 `is_online` 을 함께 읽는 유일한 소유자다.
//
// 두 값을 따로 보면 **죽은 워커가 일하는 것처럼** 보인다. offline 워커의 `current_job` 은
// 지금 처리 중인 작업이 아니다. 마지막으로 가지고 있던 작업이다. online 을 먼저 보지 않으면
// 목록, 상세, 정렬이 각자 다른 결론을 낸다.
//
// 소비처는 wire 타입이 아니라 행 클래스를 가진다(`current_job` 을 이미 `?? null` 로
// 정규화한다). 그래서 인자를 구조 타입으로 받는다.
export type WorkerWorkState = "offline" | "idle" | "working";

export type WorkerWorkAxes = {
  online: boolean;
  current_job: WorkerCurrentJob | null;
};

export function workerWorkState(worker: WorkerWorkAxes): WorkerWorkState {
  if (!worker.online) {
    return "offline";
  }
  return worker.current_job === null ? "idle" : "working";
}

// 작업 경과("N분째 처리 중")를 셀 수 있는지 판정한다. online 이고 실제로 job 을 가지고 있을
// 때만 센다. offline 워커의 경과를 계속 늘리면 멈춘 작업이 진행 중으로 보인다.
export function canShowJobElapsed(worker: WorkerWorkAxes): boolean {
  return workerWorkState(worker) === "working";
}

// admin 전용이다. 보존 윈도우(12h) 안에 보고한 워커들의 최근 disk/CPU status와 현재 처리 중인
// job을 조회한다. offline 워커는 마지막 상태로 포함한다.
export async function listWorkerStatuses(): Promise<WorkerStatusListResp> {
  const response = await apiClient.get<WorkerStatusListResp>(apiRoutes.admin.workersStatus);
  return response.data;
}
